import { AppBar, Box, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { Typography } from '@peritasai/ui-components';
import { navigationTokens } from '@peritasai/ui-shared';
import { useLocation, useNavigate } from '@tanstack/react-router';
import React from 'react';
import PeriversLogoWhite from '../../../../assets/svg/perivers-logo-white.svg';
import { useAuthContext } from '../../../auth';
import { PeriverseHeaderSearch } from './periverse-header-search';
import { UserProfileDropdown } from '../user-profile';
import { navbarProjectTitleStyle, navbarTitleContainerStyle } from './styles';
import { useTrackAssetSearch } from './hooks';

interface PeriverseHeaderProps {
  currentPageTitle?: string;
  onToggleSidebar?: () => void;
}

export const PeriverseHeader: React.FC<PeriverseHeaderProps> = () => {
  const { user } = useAuthContext();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const {
    handleAssetTypeSelect,
    handleClearSearch,
    handleSearchChange,
    handleSearchFocus,
    handleSearchSubmit,
    isDropdownOpen,
    searchValue,
    setIsDropdownOpen,
  } = useTrackAssetSearch({ location, navigate });

  const shouldShowDropdown =
    isDesktop && searchValue.trim().length === 0 && isDropdownOpen;
  if (!user) return null;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: navigationTokens.surface,
        borderBottom: `0.06rem solid ${navigationTokens.divider}`,
        height: '5rem',
        zIndex: theme.zIndex.appBar,
        marginLeft: {
          md: isDesktop ? '72px' : 0,
        },
        width: {
          md: isDesktop ? 'calc(100% - 72px)' : '100%',
        },
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          height: '5rem',
          paddingLeft: { xs: '1.5rem', md: '1.5rem', lg: '3rem' },
          paddingRight: { xs: '1.5rem', md: '1.5rem', lg: '3rem' },
          justifyContent: 'space-between',
          backgroundColor: 'transparent',
        }}
      >
        {/* Left side - Different layouts for Desktop vs Tablet/Mobile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Tablet/Mobile Layout */}
          {!isDesktop && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <img
                  src={PeriversLogoWhite}
                  alt="PeriVerse"
                  style={{ width: 32, height: 32 }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 500,
                      lineHeight: '20px',
                      letterSpacing: '0.15px',
                      fontSize: '16px',
                      color: navigationTokens.title,
                    }}
                  >
                    PeriVerse
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: navigationTokens.brandText,
                      fontFeatureSettings: '"liga" off, "clig" off',
                      fontFamily: 'var(--fontFamily, "Helvetica Neue")',
                      fontSize: '8px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '10px',
                      letterSpacing: '0.4px',
                    }}
                  >
                    PeritasAI
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          {/* Desktop Layout */}
          {isDesktop && (
            <>
              <Box sx={navbarTitleContainerStyle}>
                <Typography component="span" sx={navbarProjectTitleStyle}>
                  PeriVerse
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Right side - Search Bar + User Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {isDesktop && (
            <PeriverseHeaderSearch
              onAssetTypeSelect={handleAssetTypeSelect}
              onClearSearch={handleClearSearch}
              onSearchChange={handleSearchChange}
              onSearchFocus={handleSearchFocus}
              onSearchKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSearchSubmit();
                }
              }}
              searchValue={searchValue}
              setIsDropdownOpen={setIsDropdownOpen}
              shouldShowDropdown={shouldShowDropdown}
            />
          )}

          {!isDesktop && (
            <UserProfileDropdown
              isCompact
              showNavigationItems
              containerSx={{ width: '40px', justifyContent: 'center' }}
              menuOffsetY="6px"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            />
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};
